import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Style/common.css';
import "./Style/header.css";
import './Style/StudentActivityReport.css';
import './Style/trophy_club.css';
import trophy_club_0 from './Image/trophy_club/trophy_club_0.png';
import Header from '../../Common/Header';
import Section from '../../Common/Section';
import SubHeader from '../../Common/SubHeader';
import Loading from '../../Common/Loading';

interface StudentActivity {
  activity_icon: string;
  activity_name: string;
  grade_level: string;
  score_achieved: string;
  time_taken: string;
}

interface ActivitySummary {
  average_score: number;
  total_activities: number;
  activities_completed?: number;
  total_time_taken: string;
}

interface WeekData {
  date: string;
  className: string;
  display: string;
}

interface ManualLessonData {
  activity_icon: string;
  activity_name: string;
  grade_level: string;
  score_achieved: string;
  time_taken: string;
}

interface TrophyData {
  english: { visible: boolean; gold: boolean };
  maths: { visible: boolean; gold: boolean };
  science: { visible: boolean; gold: boolean };
}

const StudentActivityReport: React.FC = () => {
  const navigate = useNavigate();
  const CurrentAvatar = sessionStorage.getItem("CurrentAvatar");
  const nowDate = new Date();
  const [isLegendVisible, setIsLegendVisible] = useState(false);

  const [userName, setUserName] = useState<string>("");
  const [myPoints] = useState<number>(6405);
  const [overallWeeklyProgress, setOverallWeeklyProgress] = useState<number>(0);
  const [trophyData, setTrophyData] = useState<TrophyData>({
    english: { visible: false, gold: false },
    maths: { visible: false, gold: false },
    science: { visible: false, gold: false }
  });

  const [selectedOption, setSelectedOption] = useState<string>("Weekly");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [weekNumber, setWeekNumber] = useState<number>(1);

  const [currentSubject, setCurrentSubject] = useState<string>(
    sessionStorage.getItem("Subject") || "Mathematics"
  );

  const [wlpSummary, setWlpSummary] = useState<ActivitySummary | null>(null);
  const [wlpTableData, setWlpTableData] = useState<StudentActivity[]>([]);
  const [weeks, setWeeks] = useState<WeekData[]>([]);
  const [avgScore, setAvgScore] = useState<number | null>(null);
  const [manualSummary, setManualSummary] = useState<ActivitySummary | null>(null);
  const [manualTableData, setManualTableData] = useState<ManualLessonData[]>([]);

  const [viewFromExpandedData, setViewFromExpandedData] = useState<any[]>([]);

  const [wlpExpanded, setWlpExpanded] = useState<boolean>(false);
  const [manualExpanded, setManualExpanded] = useState<boolean>(false);
  const [betweenExpanded, setBetweenExpanded] = useState(false);

  const [loadingWLP, setLoadingWLP] = useState(true);
  const [loadingManual, setLoadingManual] = useState(true);
  const [loadingWeeklyProgress, setLoadingWeeklyProgress] = useState(true);
  const [loadingWeeks, setLoadingWeeks] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showBackToBottom, setShowBackToBottom] = useState(true);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const LooginRaw = sessionStorage.getItem("Login");
  const Loogin = LooginRaw ?? "";

  const apiUrl =
    process.env.REACT_APP_DOCUMENT_LINK;

  const SchoolCode = sessionStorage.getItem("SchoolCode") || "DLR_KE_AU";
  const FamilyCode = sessionStorage.getItem("FamilyCode") || "vtlt";
  const getApiParams = useCallback(() => ({
    school_code: SchoolCode,
    family_code: FamilyCode,
    login: Loogin,
    subject: currentSubject,
    week_number: weekNumber.toString(),
    view_weekly: selectedOption === "Weekly" ? "True" : "False",
    from_date: fromDate,
    to_date: toDate
  }), [Loogin, currentSubject, weekNumber, selectedOption, fromDate, toDate]);



  const findScrollContainer = (): HTMLElement => {
    const candidates: (HTMLElement | null)[] = [
      (document.scrollingElement as HTMLElement) || null,
      document.getElementById("contents_math"),
      document.getElementById("main_top_container"),
      document.getElementById("contents_english"),
      document.getElementById("contents_physics"),
      document.getElementById("contents_chemistry"),
      document.getElementById("contents_biology"),
      document.body,
      document.documentElement,
    ];

    for (const el of candidates) {
      if (!el) continue;
      try {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if (el.scrollHeight > el.clientHeight && (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay" || overflowY === "visible")) {
          return el;
        }
      } catch (e) {
      }
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  };

  // detect and store the scroll container so click handlers can scroll the same element
  scrollContainerRef.current = findScrollContainer();
  const registeredContainer = scrollContainerRef.current;

  const handleScroll = () => {
    const el =
      scrollContainerRef.current ||
      registeredContainer ||
      (document.scrollingElement as HTMLElement) ||
      document.documentElement;

    const scrollTop = el.scrollTop;
    const innerH = el.clientHeight;
    const scrollHeight = el.scrollHeight;

    const scrollable = Math.max(0, scrollHeight - innerH);
    const TOL = 8;

    const atTop = scrollTop <= TOL;
    const atBottom = scrollTop >= Math.max(0, scrollable - TOL);

    // --- FINAL LOGIC ---
    if (atTop) {
      // Only show Back to Bottom at the top
      setShowBackToTop(false);
      setShowBackToBottom(true);
      return;
    }

    if (atBottom) {
      // Only show Back to Top at the bottom
      setShowBackToTop(true);
      setShowBackToBottom(false);
      return;
    }

    // In the middle: show only one depending on scroll direction
    const ratio = scrollable > 0 ? scrollTop / scrollable : 0;

    if (ratio < 0.5) {
      setShowBackToBottom(true);
      setShowBackToTop(false);
    } else {
      setShowBackToTop(true);
      setShowBackToBottom(false);
    }
  };

  try {
    if (registeredContainer) {
      registeredContainer.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
  } catch (e) {
    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  // run once to set initial state
  setTimeout(handleScroll, 0);
  // Monitor subject changes from sessionStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const storedSubject = sessionStorage.getItem("Subject") || "Mathematics";
      if (storedSubject !== currentSubject) {
        // console.log(`Subject changed from ${currentSubject} to ${storedSubject}`);
        setCurrentSubject(storedSubject);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [currentSubject]);

  const fetchWeeklyProgress = useCallback(async () => {
    setLoadingWeeklyProgress(true);
    try {
      const params = new URLSearchParams({
        school_code: SchoolCode,
        family_code: FamilyCode,
        login: Loogin
      });

      const response = await fetch(`${apiUrl}weekly_progress_data/?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch weekly progress");

      const result = await response.json();
      setOverallWeeklyProgress(result.overall_weekly_progress ?? 0);
      if (result.trophy_data) setTrophyData(result.trophy_data);
    } catch (error) {
      console.error("Error fetching weekly progress:", error);
      setOverallWeeklyProgress(0);
    } finally {
      setLoadingWeeklyProgress(false);
    }
  }, [Loogin, apiUrl]);

  const fetchWLPCompletedData = useCallback(async () => {
    try {
      const params = new URLSearchParams(getApiParams());
      const response = await fetch(`${apiUrl}wlpcompletedreport/?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch WLP Completed data");
      const result = await response.json();
      setViewFromExpandedData(result.expanded_table ?? []);
    } catch (error) {
      console.error(error);
      setViewFromExpandedData([]);
    }
  }, [getApiParams, apiUrl]);

  const fetchWLPData = useCallback(async () => {
    setLoadingWLP(true);
    try {
      const params = new URLSearchParams(getApiParams());

      let url = "";
      if (selectedOption === "Between") {
        url = `${apiUrl}student_activity_report_updated/?${params.toString()}`;
      } else {
        url = `${apiUrl}student-activity-report/?${params.toString()}`;
      }

      const response = await fetch(url);
      // console.log("URL:", url);
      // console.log("Response:", response);
      if (!response.ok) throw new Error("Failed to fetch WLP data");

      const result = await response.json();

      const totalSeconds = result.total_time_seconds || 0;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      setWlpSummary({
        average_score: result.average_score ?? 0,
        total_activities: result.total_activities ?? 0,
        activities_completed: result.activities_completed ?? 0,
        total_time_taken: `${hours}hr ${minutes}mins`
      });

      if (selectedOption === "Between") {
        setViewFromExpandedData(result.expanded_table ?? []);
      } else {
        const tableResponse = await fetch(`${apiUrl}weekly_lesson_plan_table/?${params.toString()}`);

        if (!tableResponse.ok) throw new Error("Failed to fetch WLP table");

        const tableResult = await tableResponse.json();
        setWlpTableData(Array.isArray(tableResult) ? tableResult : []);
      }
    } catch (error) {
      console.error("Error fetching WLP data:", error);
      // setWlpSummary({
      //   average_score: 0,
      //   total_activities: 0,
      //   activities_completed: 0,
      //   total_time_taken: "0hr 0mins"
      // });
      // setWlpTableData([]);
      // setViewFromExpandedData([]);
    } finally {
      setLoadingWLP(false);
    }
  }, [getApiParams, selectedOption, apiUrl]);

  const fetch5Weeks = useCallback(async () => {
    setLoadingWeeks(true);
    try {
      const params = new URLSearchParams({
        ...getApiParams(),
        week_number: weekNumber.toString()
      });

      const response = await fetch(`${apiUrl}previous_5_weeks/?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch 5 weeks history");

      const result = await response.json();
      setWeeks(result.weekends || []);
      setAvgScore(result.avg_score ?? null);
    } catch (error) {
      console.error("Error fetching 5 weeks history:", error);
      setWeeks([]);
      setAvgScore(null);
    } finally {
      setLoadingWeeks(false);
    }
  }, [getApiParams, weekNumber, apiUrl]);

  const fetchManualLessonsData = useCallback(async () => {
    setLoadingManual(true);
    try {
      const params = new URLSearchParams(getApiParams());

      const response = await fetch(`${apiUrl}manually_selected_lessons/?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch manual lessons");

      const result = await response.json();
      setManualSummary(result.summary ?? null);
      setManualTableData(result.table_data ?? []);
    } catch (error) {
      console.error("Error fetching manual lessons:", error);
      setManualSummary({
        average_score: 0,
        total_activities: 0,
        total_time_taken: "0hr 0mins"
      });
      setManualTableData([]);
    } finally {
      setLoadingManual(false);
    }
  }, [getApiParams, apiUrl]);

  useEffect(() => {
    // Always fetch weekly progress regardless of view mode
    fetchWeeklyProgress();
  }, [fetchWeeklyProgress]);

  useEffect(() => {
    // Fetch WLP and Manual data only when in Weekly view
    if (selectedOption === "Weekly") {
      fetchWLPData();
      fetch5Weeks();
      fetchManualLessonsData();
    }
  }, [
    currentSubject,
    weekNumber,
    selectedOption,
    fetchWLPData,
    fetch5Weeks,
    fetchManualLessonsData
  ]);

  useEffect(() => {
    // ONLY fetch data when BOTH fromDate AND toDate are provided in Between view
    if (selectedOption === "Between" && fromDate && toDate) {
      fetchWLPData();
      fetchManualLessonsData();
      fetchWLPCompletedData();
    }
  }, [
    selectedOption,
    fromDate,
    toDate,
    fetchWLPData,
    fetchManualLessonsData,
    fetchWLPCompletedData
  ]);



  const handlePreviousWeek = () => {
    setWeekNumber(prev => prev - 1);
  };

  const handleNextWeek = () => {
    if (weekNumber < 1) {
      setWeekNumber(prev => prev + 1);
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOption = event.target.value;
    setSelectedOption(newOption);

    if (newOption === "Weekly") {
      setFromDate("");
      setToDate("");
      setWeekNumber(1);
    } else {
      // Reset all data for Between mode - do NOT fetch yet, wait for user to provide both dates
      setWlpSummary({
        average_score: 0,
        total_activities: 0,
        activities_completed: 0,
        total_time_taken: "0hr 0mins"
      });
      setWlpTableData([]);
      setWeeks([]);
      setViewFromExpandedData([]);
      setManualSummary({
        average_score: 0,
        total_activities: 0,
        total_time_taken: "0hr 0mins"
      });
      setManualTableData([]);
    }
  };

  const handleDateChange = useCallback(() => {
    if (fromDate && toDate && selectedOption === "Between") {
      let validFromDate = new Date(fromDate);
      let validToDate = new Date(toDate);

      if (validFromDate > validToDate) {
        const tempDate = validFromDate;
        validFromDate = validToDate;
        validToDate = tempDate;
        setFromDate(validToDate.toISOString().split('T')[0]);
        setToDate(validFromDate.toISOString().split('T')[0]);
      }
    }
  }, [fromDate, toDate, selectedOption]);

  useEffect(() => {
    handleDateChange();
  }, [handleDateChange]);

  useEffect(() => {
    const UserName = sessionStorage.getItem('FName');
    if (UserName) {
      setUserName(UserName);
    }

    document.body.classList.add('StudyRoom-background');
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundColor = '#ffffff';

    const handleResize = () => {
      document.body.style.height = `${window.innerHeight - 40}px`;
      document.body.style.width = `${window.innerWidth}px`;
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.backgroundSize = 'cover';
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      document.body.classList.remove('StudyRoom-background');
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isPageLoading = loadingWLP || loadingManual || loadingWeeklyProgress || loadingWeeks;

  useEffect(() => {
    const handleClickOutside = () => {
      setIsLegendVisible(false);
    };

    if (isLegendVisible) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLegendVisible]);

  const formatWeekEndingDate = () => {
    const currentDate = new Date();
    const daysToAdd = (-currentDate.getDay() + 7 * weekNumber);
    const weekEndingDate = new Date(currentDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
    return `Week ending ${weekEndingDate.toLocaleDateString('en-GB')}`;
  };

  const showNextWeekButton = () => {
    return weekNumber !== 1;
  };

  const formatDate = (date: string) => {
    if (!date) return "-";

    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };


  return (
    <>
      {isPageLoading && <Loading />}

      <form>

        <div id="main_top_container">
          <Header />
          <a
            href="#bottom"
            className="back-to-bottom"
            onClick={(e) => {
              e.preventDefault();
              const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
              try {
                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
              } catch {
                window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
              }
              setTimeout(() => {
                (container as any).scrollTop = (container as any).scrollHeight;
                (document.body as any).scrollTop = document.body.scrollHeight;
              }, 400);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                try {
                  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                } catch {
                  window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
                }
                setTimeout(() => {
                  (container as any).scrollTop = (container as any).scrollHeight;
                  (document.body as any).scrollTop = document.body.scrollHeight;
                }, 400);
              }
            }}
            aria-label="Back to bottom"
            role="button"
            tabIndex={0}
            style={{
              opacity: showBackToBottom ? 1 : 0,
              transform: showBackToBottom ? 'translateY(0)' : 'translateY(8px)',
              display: 'block',
              cursor: 'pointer',
              zIndex: 2147483647,
              pointerEvents: showBackToBottom ? 'auto' : 'none',
              position: 'fixed',
              left: 20,
              bottom: 20,
              transition: 'opacity 240ms ease, transform 240ms ease',


              boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            Back to Bottom
          </a>

          <a
            href="#top"
            className="back-to-top"
            onClick={(e) => {
              e.preventDefault();
              const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
              try {
                container.scrollTo({ top: 0, behavior: 'smooth' });
              } catch {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              setTimeout(() => {
                (container as any).scrollTop = 0;
                (document.body as any).scrollTop = 0;
              }, 300);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                try {
                  container.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setTimeout(() => {
                  (container as any).scrollTop = 0;
                  (document.body as any).scrollTop = 0;
                }, 300);
              }
            }}
            aria-label="Back to top"
            role="button"
            tabIndex={0}
            style={{
              opacity: showBackToTop ? 1 : 0,
              transform: showBackToTop ? 'translateY(0)' : 'translateY(8px)',
              display: 'block',
              cursor: 'pointer',
              zIndex: 2147483647,
              pointerEvents: showBackToTop ? 'auto' : 'none',
              position: 'fixed',
              right: 20,
              bottom: 20,
              transition: 'opacity 240ms ease, transform 240ms ease',
              boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            Back to Top
          </a>
          <Section />
        </div>

        <SubHeader isMSRPage={true} pageType="studentactivity">

          <div className="section-container" id="student-reports-collapse">
            <div className="content-container">
              <div className="content-top">
                <div className="report-heading-container">
                  {currentSubject !== "Science" && (
                    <div
                      id="linkbtnReportLeft"
                      className="report-arrow-left"
                      onClick={() => navigate(currentSubject === "English" ? "/STUDENT/MSR/StudentEnglishCheckReport" : "/STUDENT/MSR/StudentMathsDoctorReport")}
                      style={{ cursor: 'pointer' }}
                    ></div>
                  )}
                  <label id="lblReportText" className="report-heading">Student Reports</label>
                  {currentSubject !== "Science" && (
                    <div
                      id="linkbtnReportRight"
                      className="report-arrow-right"
                      onClick={() => navigate(currentSubject === "English" ? "/STUDENT/MSR/StudentEnglishCheckReport" : "/STUDENT/MSR/StudentMathsDoctorReport")}
                      style={{ cursor: 'pointer' }}
                    ></div>
                  )}
                </div>
                <div className="report-displayed-select">
                  <div className="report-displayed-heading">Report Displayed:</div>
                  <div className="radio-mobile-container">
                    <div className="radio-container">
                      <input
                        type="radio"
                        id="rdoWeekly"
                        name="toggler"
                        value="Weekly"
                        checked={selectedOption === "Weekly"}
                        onChange={handleRadioChange}
                        style={{ cursor: "pointer" }}
                      />
                      <label htmlFor="rdoWeekly" style={{ cursor: "pointer" }}>Weekly</label>
                    </div>
                    <div className="radio-container">
                      <input
                        type="radio"
                        id="rdoViewFrom"
                        name="toggler"
                        value="Between"
                        checked={selectedOption === "Between"}
                        onChange={handleRadioChange}
                        style={{ cursor: "pointer" }}
                      />
                      <label htmlFor="rdoViewFrom" style={{ cursor: "pointer" }}>View From:</label>
                    </div>
                    <input
                      type="date"
                      id="txtCalFromDate"
                      placeholder="dd/mm/yyyy"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      disabled={selectedOption !== "Between"}
                      max={new Date().toISOString().split("T")[0]}
                    />

                    <span>To:</span>
                    <input
                      type="date"
                      id="txtCalToDate"
                      placeholder="dd/mm/yyyy"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      disabled={selectedOption !== "Between"}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>
                {selectedOption === "Weekly" && (
                  <div id="divWeekEndingText" className="week-ending-container">
                    <div
                      className="arrow-left"
                      id="linkbtnPreviousWeek"
                      onClick={handlePreviousWeek}
                    ></div>
                    <div className="wk-ending-text" id="divWeekEndingDate">
                      {formatWeekEndingDate()}
                    </div>
                    {showNextWeekButton() && (
                      <div
                        className="arrow-right"
                        id="linkbtnNextWeek"
                        onClick={handleNextWeek}
                      ></div>
                    )}
                  </div>
                )}
              </div>

              <div id="divWLPSection" className="student-report-left">
                <div id="divWLPViewWeekly" className="widget-container toggle-wlp">
                  <div className="widget-wrapper">
                    <div className="heading">
                      {selectedOption === "Weekly" ? "Weekly Lesson Plan Summary" : " Weekly Lesson Plan Summary"}
                      {selectedOption === "Between" && fromDate && toDate && (
                        <span className="date-info between-dates">
                          | {formatDate(fromDate)} - {formatDate(toDate)}
                        </span>
                      )}
                    </div>

                    <div className="wlp-report-weekly">
                      <div className="circles-container">
                        <div className="progress-circles" id="divWeeklyWLPAverageScoreCanvas">
                          <div className="animated-circles-text">
                            <div className="blue-circle-large-text" id="divWLPSummaryAverageScore">
                              <strong style={{ fontSize: '32px' }}>
                                {wlpSummary ? `${wlpSummary.average_score}%` : '0%'}
                              </strong>
                            </div>
                            <br />
                            <div className="larger-small-text">Average Score</div>
                          </div>
                        </div>
                        <div className="progress-circles purple-circle">
                          <div className="filled-circles-text">
                            <div id="divWLPSummaryTotalActivities" className="purple-circle-large-text">
                              <p style={{ fontSize: '30px', fontFamily: 'system-ui', lineHeight: '1.2', fontWeight: 'normal', textShadow: '1px 1px 1px #ffffffff' }}>
                                {wlpSummary ? `${wlpSummary.activities_completed || 0}/${wlpSummary.total_activities}` : '0/0'}
                              </p>
                            </div>
                            <br />
                            <div className="circle-small-text">Total Activities</div>
                          </div>
                        </div>
                        <div className="progress-circles blue-circle">
                          <div className="filled-circles-text">
                            <div id="divWLPSummaryTotalTimeTaken" className="blue-circle-large-text">
                              <p style={{ fontSize: '22px', fontFamily: 'system-ui', lineHeight: '1.2', fontWeight: 'normal', textShadow: '1px 1px 1px #ffffffff' }}>
                                {wlpSummary ? wlpSummary.total_time_taken : '0hr 0mins'}
                              </p>
                            </div>
                            <br />
                            <div className="circle-small-text">Total Time Taken</div>
                          </div>
                        </div>
                      </div>

                      {selectedOption === "Weekly" && (
                        <>
                          <div className="expanded-container">
                            <div className="five-weeks-container">
                              <div className="prev-5-heading">Previous 5 Weeks History</div>
                              <table className="prev-5-table">
                                <thead>
                                  <tr>
                                    {weeks.map((week, i) => (
                                      <th key={i}>{week.date}</th>
                                    ))}
                                    <th className="history-avg">Avg Score</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    {weeks.map((week, i) => (
                                      <td key={i}>
                                        <div className={week.className}>
                                          {week.display || (week.className === "center" ? "-" : "")}
                                        </div>
                                      </td>
                                    ))}
                                    <td className="history-avg">{avgScore !== null && avgScore !== undefined ? avgScore : '-'}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="show-hide-btn" id="wlp-weekly-btn">
                            <a
                              href="#"
                              className="btn blue"
                              onClick={(e) => {
                                e.preventDefault();
                                setWlpExpanded(!wlpExpanded);
                              }}
                              style={{ height: '33px' }}
                            >
                              {wlpExpanded ? 'Hide Details' : 'Show Details'}
                              <span className="btn-expand">{wlpExpanded ? '-' : '+'}</span>
                            </a>
                          </div>
                          {wlpExpanded && (
                            <div className="report-table-container" id="wlp-weekly-expanded">
                              <div className="wlp-table-container">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Activity</th>
                                      {/* <th>Grade Level</th> */}
                                      <th>Score Achieved</th>
                                      <th>Time Taken (mins)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {wlpTableData.length > 0 ? (
                                      wlpTableData.map((row, index) => (
                                        <tr key={index}>
                                          <td>
                                            <div className={row.activity_icon}></div>
                                            <span>{row.activity_name || "-"}</span>
                                          </td>
                                          {/* <td className="table-column-centered">{row.grade_level || "-"}</td> */}
                                          <td className="table-column-centered">{row.score_achieved || "-"}</td>
                                          <td className="table-column-centered">{row.time_taken || "-"}</td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={4} style={{ textAlign: 'center' }}>No data available</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {selectedOption === "Between" && viewFromExpandedData.length > 0 && (
                        <>
                          <br></br>
                          <a
                            href="#"
                            className="btn blue"
                            onClick={(e) => {
                              e.preventDefault();
                              setWlpExpanded(!wlpExpanded);
                            }}
                            style={{ height: '33px' }}
                          >
                            {wlpExpanded ? 'Hide Details' : 'Show Details'}
                            <span className="btn-expand">{wlpExpanded ? '-' : '+'}</span>
                          </a>
                          {wlpExpanded && (
                            <div className="report-table-container">
                              <div className="wlp-table-container">
                                <div style={{ fontSize: "16px", textAlign: "center", marginTop: "25px", fontWeight: "600", marginBottom: "20px" }}>Weekly Lesson Plan Details  |  -</div>
                                <table>

                                  <thead>
                                    <tr>
                                      <th>WLP Completed</th>
                                      <th>Week Ending</th>
                                      <th>Average Score</th>
                                      <th>Activities Completed</th>
                                      <th>Time Taken (h:m:s)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {viewFromExpandedData.map((row, idx) => (
                                      <tr key={idx}>
                                        <td style={{ textAlign: "center" }}>
                                          <div
                                            className={row.wlp_completed}
                                            style={{ display: "inline-block" }}
                                          ></div>
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          {row.weekend_date || "-"}
                                        </td>
                                        <td style={{ textAlign: "center" }}>{row.average_score || "-"}</td>
                                        <td style={{ textAlign: "center" }}>{row.activities_completed || "-"}</td>
                                        <td style={{ textAlign: "center" }}>{row.time_taken || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div id="divManualSection" className="student-report-left">
                <div className="widget-container">
                  <div className="widget-wrapper">
                    <div className="heading">
                      Manually Selected Lessons
                      {selectedOption === "Between" && fromDate && toDate && (
                        <div className="date-info between-dates">
                          | {formatDate(fromDate)} - {formatDate(toDate)}
                        </div>
                      )}
                    </div>
                    <div className="manual-selection-container">
                      <div className="expanded-container">
                        <div className="circles-container">
                          <div className="progress-circles" id="divManuallySelectedLessonsAverageScoreCanvas">
                            <div className="animated-circles-text">
                              <div className="blue-circle-large-text">
                                <strong style={{ fontSize: '32px' }}>
                                  {manualSummary ? `${manualSummary.average_score}%` : '0%'}
                                </strong>
                              </div>
                              <br />
                              <div className="larger-small-text">Average Score</div>
                            </div>
                          </div>
                          <div className="progress-circles purple-circle">
                            <div className="filled-circles-text">
                              <div className="purple-circle-large-text">
                                <p style={{ fontSize: '30px', fontFamily: 'system-ui', lineHeight: '1.2', fontWeight: 'normal', textShadow: '1px 1px 1px #ffffffff' }}>
                                  {manualSummary ? manualSummary.total_activities : '0'}
                                </p>
                              </div>
                              <br />
                              <div className="circle-small-text">Total Activities</div>
                            </div>
                          </div>
                          <div className="progress-circles blue-circle">
                            <div className="filled-circles-text">
                              <div className="blue-circle-large-text">
                                <p style={{ fontSize: '22px', fontFamily: 'system-ui', lineHeight: '1.2', fontWeight: 'normal', textShadow: '1px 1px 1px #ffffffff' }}>
                                  {manualSummary ? manualSummary.total_time_taken : '0hr 0mins'}
                                </p>
                              </div>
                              <br />
                              <div className="circle-small-text">Total Time Taken</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="show-hide-btn" id="manual-selection-btn">
                        <a
                          href="#"
                          className="btn blue"
                          onClick={(e) => {
                            e.preventDefault();
                            setManualExpanded(!manualExpanded);
                          }}
                          style={{ height: '33px' }}
                        >
                          {manualExpanded ? 'Hide Details' : 'Show Details'}
                          <span className="btn-expand">{manualExpanded ? '-' : '+'}</span>
                        </a>
                      </div>

                      {manualExpanded && (
                        <div className="report-table-container" id="manual-selection-expanded">
                          <div className="wlp-table-container">
                            <table>
                              <thead>
                                <tr>
                                  <th>Activity<div className="sort sort-down"></div></th>
                                  {/* <th>Grade Level<div className="sort sort-down"></div></th> */}
                                  <th>Score Achieved<div className="sort sort-down"></div></th>
                                  <th>Time Taken (mins)<div className="sort sort-down"></div></th>
                                </tr>
                              </thead>
                              <tbody>
                                {manualTableData.length > 0 ? manualTableData.map((row, index) => (
                                  <tr key={index}>
                                    <td>
                                      <div className={row.activity_icon}></div>
                                      <span>{row.activity_name || "-"}</span>
                                    </td>
                                    {/* <td className="table-column-centered">{row.grade_level || "-"}</td> */}
                                    <td className="table-column-centered">{row.score_achieved || "-"}</td>
                                    <td className="table-column-centered">{row.time_taken || "-"}</td>
                                  </tr>
                                )) : (
                                  <tr>
                                    <td colSpan={4} style={{ textAlign: 'center' }}>No data available</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>


              {/* <div id="divSetWorkSection" className="student-report-left" style={{ marginBottom: "44px" }}>
      <div className="widget-container">
        <div className="widget-wrapper">
          <div className="heading">
            Set Work
            <div id="divSetWorkBetweenDates" className="date-info between-dates">
            </div>
          </div>

          <div className="wlp-report-between">
            <div className="expanded-container">
              <div className="circles-container">

                <div className="progress-circles" id="divSetWorkSummaryNoOfCompletedCanvas">
                  <div className="animated-circles-text">
                    <div id="divSetWorkSummaryNoOfCompleted" className="blue-circle-large-text"></div>
                    <br />
                    <div className="circle-small-text">
                      Set Works
                      <br />
                      Completed
                    </div>
                  </div>
                </div>

                <div className="progress-circles" id="divSetWorkSummaryAverageScoreCanvas">
                  <div className="animated-circles-text">
                    <div id="divSetWorkSummaryAverageScore" className="blue-circle-large-text"></div>
                    <br />
                    <div className="larger-small-text">Average Score</div>
                  </div>
                </div>

                <div className="progress-circles purple-circle">
                  <div className="filled-circles-text">
                    <div id="divSetWorkSummaryTotalActivities" className="purple-circle-large-text">0</div>
                    <br />
                    
                    <div className="circle-small-text">Total Activities</div>
                  </div>
                </div>

                <div className="progress-circles blue-circle">
                  <div className="filled-circles-text">
                    <div id="divSetWorkSummaryTotalTimeTaken" className="blue-circle-large-text">0hr 0mins</div>
                    <br />
                    <div className="circle-small-text">Total Time Taken</div>
                  </div>
                </div>

              </div>
            </div>

            <div className="show-hide-btn" id="set-work-btn">
              <a className="btn blue" style={{height:'33px'}}>
                Show Details<span className="btn-expand">+</span>
              </a>
            </div>

            <div className="report-table-container" id="set-work-expanded">
              <div className="wlp-table-container">
                <table>
                  <thead>
                    <tr>
                      <th className="header-double-line">Set Work Title</th>
                      <th className="table-column-centered">Score</th>
                      <th className="header-double-line table-column-centered">
                        <span className="ml-minus-20">Activities Completed</span>
                      </th>
                      <th className="table-column-centered">Due Date</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div> */}

            </div>
          </div>
        </SubHeader>
      </form>
    </>
  );
};

export default StudentActivityReport;